import {
  AOOSData,
  SnoozedItemsData,
} from '@buncha/itemDetails/types/SubstituteItemDetails';
import {ActivityIndicator, Dimensions, StyleSheet} from 'react-native';
import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H6Text} from '@buncha/components/core/text';
import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {ChatModes} from '@buncha/batchDetails/types/Chat';
import ChatOpener from '@buncha/components/composites/ChatOpener';
import {CustomImage} from '@buncha/components/core/Image';
import {Divider} from 'native-base';
import If from '@buncha/components/conditional/If';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import {Modules} from '@buncha/utils/types';
import {PButton} from '@buncha/components/core/Button';
import ReplacementModalBody from '../itemReplacement/replacementModalBody';
import {StorageKeys} from '@buncha/config/storage/type';
import Touchable from '@buncha/components/core/touchable/Touchable';
import {UserTypes} from '@buncha/onBoarding/types';
import {navigation} from '@buncha/config/navigation';
import {storage} from '@buncha/config/storage';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';
import {useSubstituteItemDetailsContext} from '@buncha/itemDetails/hooks/SubstituteItemDetail';

interface Props {
  handleAdjacentItemFound?: () => void;
  handleOpenDropModal?: () => void;
  isLoading?: boolean;
  aOOSData: AOOSData;
  storeLogo: string | undefined;
  newQuantity: number | undefined;
  oldQuantity: number | undefined;
  adjacentOOSId: number | undefined;
}

const SubstituteDetails = (props: Props) => {
  const {
    handleAdjacentItemFound,
    handleOpenDropModal,
    isLoading,
    aOOSData,
    storeLogo,
    adjacentOOSId,
  } = props;
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const {state} = useSubstituteItemDetailsContext();
  const {body} =
    useGetLocale(
      Modules.itemDetails,
      ItemDetailsScreen.SubstituteItemDetailPage,
    ) ?? {};
  const AOOS_SNOOZE_TIMER = useAppSelector(
    gState => gState.onboarding.constants?.AOOS_SNOOZE_TIMER,
  );
  const shopper1Name = aOOSData?.originalShopper?.firstName;
  const conversationId = aOOSData?.conversationId;
  const isBackButtonEnabled =
    state?.constants?.ADJ_OOS_BACK_NAVIGATION === 'true';
  const oldItem = {...aOOSData?.originalItem, quantity: props.oldQuantity};
  const newItem = {...aOOSData?.newItem, quantity: props.newQuantity};
  const [snoozedItemsData, setSnoozedItemsData] = useState<SnoozedItemsData>(
    {},
  );
  const timerMinutes = parseInt(AOOS_SNOOZE_TIMER ?? '', 10) || 5;

  const onSnooze = useCallback(async () => {
    const timeStamp = new Date().toISOString();
    const storedData = await storage.getItem(StorageKeys.snoozeTimer);
    const existingSnooze = JSON.parse(storedData || '{}');
    const existingSnoozedItems = snoozedItemsData;
    if (!adjacentOOSId) return;
    existingSnooze[adjacentOOSId] = timeStamp;
    existingSnoozedItems[adjacentOOSId] = timeStamp;
    await storage.setItem(
      StorageKeys.snoozeTimer,
      JSON.stringify(existingSnooze),
    );
    await storage.setItem(
      StorageKeys.snoozedItems,
      JSON.stringify(existingSnoozedItems),
    );
    navigation.goBack();
  }, [adjacentOOSId, snoozedItemsData]);

  const fetchSnoozedItems = useCallback(async () => {
    const storedData = await storage.getItem(StorageKeys.snoozedItems);
    const existingSnoozedItems = JSON.parse(storedData || '{}');
    setSnoozedItemsData(existingSnoozedItems);
  }, []);

  useEffect(() => {
    fetchSnoozedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <Divider
        style={[
          styles.divider,
          {
            backgroundColor: theme?.background?.gray?.[200],
          },
        ]}
      />
      <Box>
        <HBox
          style={[
            styles.lightBorderBox,
            styles.shopperDetails,
            {
              borderColor: theme?.text?.gray?.[200],
            },
          ]}>
          <HBox style={styles.shopperDetailsInnerBox}>
            <CustomImage
              source={storeLogo ?? ''}
              resizeMode="contain"
              style={[
                styles.storeLogo,
                {
                  borderColor: theme?.text?.gray?.[200],
                },
              ]}
              onErrorSource={''}
            />
            <Box>
              <H6Text
                style={[
                  styles.textAlignLeft,
                  {
                    fontFamily: muliFont[600].normal,
                    color: theme?.text?.blue[300],
                  },
                ]}>
                {body?.sentBy?.replace?.('{shopper1Name}', shopper1Name)}
              </H6Text>
              <CustomText
                style={[
                  styles.textAlignLeft,
                  {
                    fontFamily: muliFont[600].normal,
                  },
                ]}>
                {body?.chatWith?.replace?.('{shopper1Name}', shopper1Name)}
              </CustomText>
            </Box>
          </HBox>
          <ChatOpener
            hideRole={UserTypes.shopper}
            mode={ChatModes.order}
            conversationId={conversationId}
            height={35}
            width={50}
            style={styles.chatIcon}
            iconWidth={130}
          />
        </HBox>
        <Box>
          <ReplacementModalBody
            text=""
            item={oldItem}
            catalogItem={newItem}
            rightText={body?.similarItem}
            itemSubImage={aOOSData?.originalStore?.storeLogoUrl}
            newItemSubImage={aOOSData?.newStore?.storeLogoUrl}
          />
        </Box>
        <Box
          style={[
            styles.buttonBox,
            styles.lightBorderBox,
            {
              borderColor: theme?.text?.gray?.[200],
            },
          ]}>
          <H6Text
            style={[
              styles.textAlignLeft,
              {
                color: theme?.text?.blue?.[300],
              },
            ]}>
            {body?.findThisItem}
          </H6Text>

          <HBox>
            <PButton
              title={body?.no}
              style={[
                styles.leftButton,
                {borderColor: theme?.text?.gray?.[200]},
              ]}
              titleStyle={[
                {
                  fontFamily: muliFont[600].normal,
                },
              ]}
              titleColor={theme?.text?.gray?.[300]}
              borderWidth={0.5}
              borderColor={theme?.text?.blue?.[300]}
              bgColor={theme?.background.white[100]}
              onPress={handleOpenDropModal}
            />
            <PButton
              title={body?.yes}
              style={[styles.rightButton]}
              titleStyle={[
                {
                  fontFamily: muliFont[700].normal,
                },
              ]}
              disabled={isLoading}
              isLoading={isLoading}
              loadingText=""
              loadingIndicator={<ActivityIndicator />}
              bgColor={theme?.background.blue[100]}
              onPress={handleAdjacentItemFound}
            />
          </HBox>
        </Box>
        <If
          condition={
            !isBackButtonEnabled &&
            adjacentOOSId &&
            !snoozedItemsData?.[adjacentOOSId]
          }>
          <HBox style={styles.snooze}>
            <Touchable style={styles.snooze} onPress={onSnooze}>
              <H6Text
                style={[
                  styles.textAlignCenter,
                  {
                    color: theme?.text?.blue?.[100],
                  },
                ]}>
                {body?.snooze?.replace('{timer}', timerMinutes)}
              </H6Text>
            </Touchable>
          </HBox>
        </If>
      </Box>
    </Fragment>
  );
};

export default React.memo(SubstituteDetails);

export const styles = StyleSheet.create({
  storeLogo: {
    borderRadius: 50,
    height: 32,
    width: 32,
    overflow: 'hidden',
    borderWidth: 1,
    marginLeft: 5,
  },
  chatIcon: {
    height: 30,
  },
  leftButton: {
    flex: 1,
    marginRight: 5,
    borderWidth: 1,
  },
  rightButton: {
    flex: 1,
    marginLeft: 5,
  },
  divider: {
    minWidth: Dimensions.get('screen').width,
    marginLeft: -20,
    marginVertical: 15,
  },
  lightBorderBox: {
    borderWidth: 1,
    borderRadius: 10,
  },
  shopperDetails: {
    display: 'flex',
    alignItems: 'center',
    paddingVertical: '2%',
  },
  shopperDetailsInnerBox: {
    gap: 10,
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  textAlignLeft: {
    textAlign: 'left',
  },
  textAlignCenter: {
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
  snooze: {
    justifyContent: 'center',
    marginTop: 5,
  },
  buttonBox: {
    paddingTop: 10,
    paddingBottom: 15,
    paddingHorizontal: 10,
    gap: 10,
  },
});
